import React, { useEffect, useState, useCallback } from "react";
import { Outlet, useOutletContext, useLocation } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { JobEditorDialog } from "@/components/admin/JobEditorDialog";
import { EventEditorDialog } from "@/components/admin/EventEditorDialog";
import AdminChangePasswordDialog from "@/components/admin/AdminChangePasswordDialog";

// Public admin routes that must render BEFORE the login gate — the whole
// point of "forgot password" is that the user CAN'T sign in yet.
const PUBLIC_ADMIN_PATHS = new Set(["/admin/forgot-password", "/admin/reset-password"]);

/**
 * AdminShell — top-level admin route. Handles token check, data fetching,
 * and shared editors. Individual admin pages read state via `useAdminContext`.
 */
export default function AdminShell() {
    const [authed, setAuthed] = useState(!!localStorage.getItem("simonara_admin_token"));
    const [me, setMe] = useState(null); // { email, admin_role }

    const [jobs, setJobs] = useState([]);
    const [submissions, setSubmissions] = useState({ resumes: [], talent_requests: [], contacts: [], applications: [] });
    const [events, setEvents] = useState([]);
    const [passportRequests, setPassportRequests] = useState([]);
    const [loading, setLoading] = useState(false);

    const [jobDialogOpen, setJobDialogOpen] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const [meRes, jobsRes, subsRes, eventsRes, reqRes] = await Promise.all([
                api.get("/admin/me"),
                api.get("/admin/jobs"),
                api.get("/admin/submissions"),
                api.get("/admin/events"),
                api.get("/admin/passport-requests"),
            ]);
            setMe(meRes.data);
            setJobs(jobsRes.data);
            setSubmissions(subsRes.data);
            setEvents(eventsRes.data);
            setPassportRequests(reqRes.data);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) {
                localStorage.removeItem("simonara_admin_token");
                setAuthed(false);
            }
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (authed) fetchAll();
    }, [authed, fetchAll]);

    // Small event bus so child dialogs (e.g. MFA setup completes) can nudge
    // us to refresh /admin/me without prop-drilling a callback all the way
    // down through AdminLayout.
    useEffect(() => {
        const h = () => { if (authed) fetchAll(); };
        window.addEventListener("admin:me-refetch", h);
        return () => window.removeEventListener("admin:me-refetch", h);
    }, [authed, fetchAll]);

    // Public routes (forgot-password, reset-password) render without the
    // login gate AND without the AdminLayout chrome so the shell doesn't
    // trigger a spurious 401 or a nav flash.
    const location = useLocation();
    if (PUBLIC_ADMIN_PATHS.has(location.pathname)) {
        return <Outlet context={{ me: null, adminRole: null }} />;
    }

    if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

    const ctx = {
        me, adminRole: me?.admin_role || "platform_admin",
        jobs, events, submissions, passportRequests, loading, fetchAll,
        openJobEditor: (job = null) => { setEditingJob(job); setJobDialogOpen(true); },
        openEventEditor: (ev = null) => { setEditingEvent(ev); setEventDialogOpen(true); },
    };

    return (
        <>
            <AdminLayout onLogout={() => setAuthed(false)} me={me}>
                <Outlet context={ctx} />
            </AdminLayout>
            <JobEditorDialog open={jobDialogOpen} onOpenChange={setJobDialogOpen} editing={editingJob} onSaved={fetchAll} />
            <EventEditorDialog open={eventDialogOpen} onOpenChange={setEventDialogOpen} editing={editingEvent} onSaved={fetchAll} />
            {/* Forced rotation after a peer-reset. Non-dismissable, blocks the
                UI until the admin sets a password only they know. The modal
                re-fetches /admin/me on close so the flag flip is visible. */}
            <AdminChangePasswordDialog
                open={!!me?.password_change_required}
                onOpenChange={(open) => { if (!open) fetchAll(); }}
                forced
            />
        </>
    );
}

export const useAdminContext = () => useOutletContext();
